import React from 'react'

/**
 * Render WhatsApp-style formatting for message previews.
 * Supports:
 * - *bold*
 * - _italic_
 * - ~strikethrough~
 * - `monospace`
 * - ```code block``` (may span lines; content is literal)
 * - "> " quote lines
 * - "* " / "- " bullet lines
 * - "1. " numbered-list lines
 * - Nested combinations like *_bold italic_*
 *
 * Safe implementation that returns React nodes without using innerHTML.
 */
export function renderWhatsAppFormatted(
  text?: string,
  features?: FormatFeature[]
): React.ReactNode {
  if (!text || typeof text !== 'string') return text || ''

  // Early exit if no formatting markers present
  if (!hasFormattingMarkers(text)) return text

  return parseWhatsAppFormat(text, toFeatureSet(features))
}

export interface FormatToken {
  type:
    | 'text'
    | 'bold'
    | 'italic'
    | 'strike'
    | 'mono'
    | 'codeblock'
    | 'quote'
    | 'bullet'
    | 'ordered'
  content: string | FormatToken[]
  // The literal delimiter characters, preserved so a consumer (the chat
  // composer) can re-render the exact input string with markers visible.
  // open + content + close always reassembles the original slice.
  open?: string
  close?: string
}

type Token = FormatToken

export type FormatFeature = Exclude<FormatToken['type'], 'text'>

export const ALL_FORMAT_FEATURES: FormatFeature[] = [
  'bold',
  'italic',
  'strike',
  'mono',
  'codeblock',
  'quote',
  'bullet',
  'ordered',
]

// What each platform actually renders — the composer and the bubbles must
// only style what the receiving app will style, everything else stays
// literal. Instagram/Messenger support bold/italic/strike/quotes but show
// backticks and list markers literally (verified on-device, ZC-3091
// follow-up); WhatsApp supports everything.
const META_MESSENGER_FEATURES: FormatFeature[] = [
  'bold',
  'italic',
  'strike',
  'quote',
]

export const FORMAT_FEATURES_BY_CHANNEL: Record<string, FormatFeature[]> = {
  whatsapp: ALL_FORMAT_FEATURES,
  facebook: META_MESSENGER_FEATURES,
  instagram: META_MESSENGER_FEATURES,
}

const toFeatureSet = (features?: FormatFeature[]): Set<FormatFeature> =>
  new Set(features ?? ALL_FORMAT_FEATURES)

const CODE_FENCE = '```'
const QUOTE_PREFIX = '> '
const BULLET_PREFIXES = ['* ', '- ']
const ORDERED_PREFIX_REGEX = /^(\d{1,3}\. )(?=\S)/

const FORMATTING_MARKERS_REGEX =
  /[*_~`]|^> |\n> |^- |\n- |^\d{1,3}\. |\n\d{1,3}\. /

function hasFormattingMarkers(text: string): boolean {
  return FORMATTING_MARKERS_REGEX.test(text)
}

/**
 * Parse WhatsApp formatting markers and return React nodes
 */
function parseWhatsAppFormat(
  text: string,
  enabled: Set<FormatFeature>
): React.ReactNode {
  const tokens = tokenize(text, true, enabled)

  // Skip React rendering when no actual formatting markers resolved.
  // Important so downstream consumers (e.g., linkify) still receive a plain string.
  if (!containsFormatting(tokens)) {
    return text
  }

  return renderTokens(tokens)
}

/**
 * Tokenize a full message, markers preserved on every token. Line-level
 * constructs (quotes, bullets, numbered lists, code fences) are only
 * recognized on the top-level pass — never inside another token's content.
 */
export function tokenizeWithMarkers(
  text: string,
  features?: FormatFeature[]
): FormatToken[] {
  if (!text || typeof text !== 'string') return []
  return tokenize(text, true, toFeatureSet(features))
}

/**
 * Tokenize text with WhatsApp formatting markers.
 * `blocks` enables line-level constructs; recursive calls pass false so a
 * span's interior never re-triggers line parsing.
 */
function tokenize(
  text: string,
  blocks: boolean,
  enabled: Set<FormatFeature>
): Token[] {
  const tokens: Token[] = []
  let i = 0
  let textBuffer = ''

  const flushTextBuffer = () => {
    if (!textBuffer) return
    tokens.push({
      type: 'text',
      content: textBuffer,
    })
    textBuffer = ''
  }

  while (i < text.length) {
    const char = text[i]
    const atLineStart = i === 0 || text[i - 1] === '\n'

    if (blocks && atLineStart) {
      const lineToken = extractLineConstruct(text, i, enabled)
      if (lineToken) {
        flushTextBuffer()
        tokens.push(lineToken.token)
        i = lineToken.endIndex
        continue
      }
    }

    // Code fence before single-backtick mono; content is literal and may
    // span lines. Only on the top-level pass, like the other blocks.
    if (
      blocks &&
      enabled.has('codeblock') &&
      char === '`' &&
      text.startsWith(CODE_FENCE, i)
    ) {
      const closeIndex = text.indexOf(CODE_FENCE, i + CODE_FENCE.length)
      const content =
        closeIndex > -1 ? text.substring(i + CODE_FENCE.length, closeIndex) : ''
      if (closeIndex > -1 && content.trim()) {
        flushTextBuffer()
        tokens.push({
          type: 'codeblock',
          content,
          open: CODE_FENCE,
          close: CODE_FENCE,
        })
        i = closeIndex + CODE_FENCE.length
        continue
      }
    }

    const delimiterType = getDelimiterType(char)
    if (delimiterType && enabled.has(delimiterType as FormatFeature)) {
      const match = extractBetween(text, i, char)
      if (match) {
        flushTextBuffer()
        tokens.push({
          type: delimiterType,
          content:
            delimiterType === 'mono'
              ? match.content
              : tokenize(match.content, false, enabled), // Recursive for nesting
          open: char,
          close: char,
        })
        i = match.endIndex + 1
        continue
      }
    }

    textBuffer += char
    i++
  }

  flushTextBuffer()

  return tokens
}

/**
 * Detect a line-level construct at a line start. The marker must be followed
 * by a space and non-empty content — "* bold" is a bullet, "*bold*" is bold
 * (its opening marker forbids a following space), "*" alone stays literal.
 */
function extractLineConstruct(
  text: string,
  startIndex: number,
  enabled: Set<FormatFeature>
): { token: Token; endIndex: number } | null {
  const lineEnd = findLineEnd(text, startIndex)
  const line = text.substring(startIndex, lineEnd)

  let type: Token['type'] | null = null
  let open = ''

  if (line.startsWith(QUOTE_PREFIX)) {
    type = 'quote'
    open = QUOTE_PREFIX
  } else {
    const bulletPrefix = BULLET_PREFIXES.find((prefix) =>
      line.startsWith(prefix)
    )
    if (bulletPrefix) {
      type = 'bullet'
      open = bulletPrefix
    } else {
      const orderedMatch = line.match(ORDERED_PREFIX_REGEX)
      if (orderedMatch) {
        type = 'ordered'
        open = orderedMatch[1]
      }
    }
  }

  if (!type || !enabled.has(type as FormatFeature)) return null

  const rest = line.substring(open.length)
  if (!rest.trim()) return null

  return {
    token: {
      type,
      content: tokenize(rest, false, enabled),
      open,
      close: '',
    },
    endIndex: lineEnd,
  }
}

function findLineEnd(text: string, from: number): number {
  const newlineIndex = text.indexOf('\n', from)
  return newlineIndex === -1 ? text.length : newlineIndex
}

/**
 * Extract content between matching delimiters
 */
function extractBetween(
  text: string,
  startIndex: number,
  delimiter: string
): { content: string; endIndex: number } | null {
  if (!isStartBoundary(text, startIndex)) {
    return null
  }

  const start = startIndex + delimiter.length
  const firstContentChar = text[start]

  // Opening delimiter must have non-whitespace content following it
  if (!firstContentChar || isForbiddenBoundaryCharacter(firstContentChar)) {
    return null
  }

  let end = start

  // Find closing delimiter (must not be at start, and must have content)
  while (end < text.length) {
    if (
      text[end] === delimiter &&
      end > start &&
      !isForbiddenBoundaryCharacter(text[end - 1]) &&
      isEndBoundary(text, end, delimiter.length)
    ) {
      const content = text.substring(start, end)
      if (!content.trim()) {
        return null
      }

      return {
        content,
        endIndex: end,
      }
    }
    end++
  }

  return null // No matching closing delimiter
}

const boldStyle = { fontWeight: 500 } as const
const italicStyle = { fontStyle: 'italic' } as const
const strikeStyle = { textDecoration: 'line-through' } as const
const monoStyle = { fontFamily: 'monospace' } as const
const codeBlockStyle = {
  fontFamily: 'monospace',
  whiteSpace: 'pre-wrap',
  background: 'rgba(0, 0, 0, 0.06)',
  borderRadius: '3px',
  padding: '0 3px',
} as const
const quoteStyle = {
  display: 'inline-block',
  borderLeft: '3px solid rgba(0, 0, 0, 0.2)',
  paddingLeft: '0.5em',
} as const

const BULLET_GLYPH = '• '

/**
 * Render tokens to React nodes
 */
function renderTokens(tokens: Token[]): React.ReactNode {
  return (
    <>
      {tokens.map((token, idx) => {
        if (token.type === 'text') {
          return <React.Fragment key={idx}>{token.content}</React.Fragment>
        }

        const inner = Array.isArray(token.content)
          ? renderTokens(token.content)
          : token.content

        if (token.type === 'bold') {
          return (
            <span key={idx} style={boldStyle}>
              {inner}
            </span>
          )
        }

        if (token.type === 'italic') {
          return (
            <span key={idx} style={italicStyle}>
              {inner}
            </span>
          )
        }

        if (token.type === 'strike') {
          return (
            <span key={idx} style={strikeStyle}>
              {inner}
            </span>
          )
        }

        if (token.type === 'mono') {
          return (
            <span key={idx} style={monoStyle}>
              {inner}
            </span>
          )
        }

        if (token.type === 'codeblock') {
          return (
            <span key={idx} style={codeBlockStyle}>
              {inner}
            </span>
          )
        }

        if (token.type === 'quote') {
          return (
            <span key={idx} style={quoteStyle}>
              {inner}
            </span>
          )
        }

        if (token.type === 'bullet') {
          return (
            <React.Fragment key={idx}>
              {BULLET_GLYPH}
              {inner}
            </React.Fragment>
          )
        }

        if (token.type === 'ordered') {
          // The number is meaningful — keep the typed marker visible.
          return (
            <React.Fragment key={idx}>
              {token.open}
              {inner}
            </React.Fragment>
          )
        }

        return null
      })}
    </>
  )
}

/**
 * Determine whether token stream contains any non-text formatting segments.
 * Used to decide if we can safely return the original string.
 */
function containsFormatting(tokens: Token[]): boolean {
  return tokens.some((token) => {
    if (token.type !== 'text') {
      return true
    }

    if (Array.isArray(token.content)) {
      return containsFormatting(token.content)
    }

    return false
  })
}

function isWordCharacter(char?: string): boolean {
  if (!char) return false
  return /[A-Za-z0-9]/.test(char)
}

function isForbiddenBoundaryCharacter(char?: string): boolean {
  if (!char) return true
  return /\s/.test(char)
}

function isStartBoundary(text: string, index: number): boolean {
  const prevChar = text[index - 1]

  if (index > 0 && isWordCharacter(prevChar)) {
    return false
  }

  return true
}

function isEndBoundary(
  text: string,
  index: number,
  delimiterLength: number
): boolean {
  const nextChar = text[index + delimiterLength]

  if (nextChar && isWordCharacter(nextChar)) {
    return false
  }

  return true
}

function getDelimiterType(char: string): Token['type'] | null {
  if (char === '*') return 'bold'
  if (char === '_') return 'italic'
  if (char === '~') return 'strike'
  if (char === '`') return 'mono'
  return null
}

/**
 * HTML-string variant of renderWhatsAppFormatted.
 * Uses the same tokenizer but returns an HTML string instead of React nodes.
 * Intended for use with dangerouslySetInnerHTML where React nodes cannot be used.
 */
export function renderWhatsAppFormattedHtml(
  text?: string,
  features?: FormatFeature[]
): string {
  if (!text || typeof text !== 'string') return text || ''
  if (!hasFormattingMarkers(text)) return escapeHtml(text)

  const tokens = tokenize(text, true, toFeatureSet(features))
  if (!containsFormatting(tokens)) return escapeHtml(text)
  return renderTokensHtml(tokens)
}

const HTML_ENTITIES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
}

// The output of this module is fed to dangerouslySetInnerHTML, so every piece
// of the source string is escaped. Escaping happens per token rather than up
// front, otherwise a quote line ("> text") would no longer tokenize.
const escapeHtml = (text: string): string =>
  text?.replace(/[&<>"']/g, (char) => HTML_ENTITIES[char]) ?? ''

function renderTokensHtml(tokens: Token[]): string {
  return tokens
    .map((token) => {
      if (token.type === 'text') return escapeHtml(token.content as string)

      const content = Array.isArray(token.content)
        ? renderTokensHtml(token.content)
        : escapeHtml(token.content as string)

      if (token.type === 'bold')
        return `<span style="font-weight:500">${content}</span>`
      if (token.type === 'italic')
        return `<span style="font-style:italic">${content}</span>`
      if (token.type === 'strike')
        return `<span style="text-decoration:line-through">${content}</span>`
      if (token.type === 'mono')
        return `<span style="font-family:monospace">${content}</span>`
      if (token.type === 'codeblock')
        return `<span style="font-family:monospace;white-space:pre-wrap;background:rgba(0,0,0,0.06);border-radius:3px;padding:0 3px">${content}</span>`
      if (token.type === 'quote')
        return `<span style="display:inline-block;border-left:3px solid rgba(0,0,0,0.2);padding-left:0.5em">${content}</span>`
      if (token.type === 'bullet') return `${BULLET_GLYPH}${content}`
      if (token.type === 'ordered')
        return `${escapeHtml(token.open || '')}${content}`
      return ''
    })
    .join('')
}
